import React from 'react';
import { Link, useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';

import { OmaLummeApp } from '../../omaLummeContext';

import styles from './styles/Navigation.module.scss';

import MobileMenuFooter from './MobileMenuFooter';
import { CampaignPriceOffer } from '../../types/contract';

type LinkProps = {
  children: any;
  onClick?: React.MouseEventHandler;
  to: string;
  id: string;
  external: boolean;
};

const NavLink: React.FunctionComponent<LinkProps> = (props: LinkProps) => {
  const href = props.to;
  const match = useMatch(`${href}/*`);

  const isActive = match || (location.pathname.includes('/article') && href === '/news');

  return (
    <Link
      to={props.to}
      onClick={props.onClick}
      className={`${styles.navLink} ${styles[props.id]} ${props.external === true ? styles.external : undefined} ${
        isActive ? `${styles.active}` : ''
      } `}
      rel={props.external === true ? 'noopener noreferrer' : ''}
      aria-current={isActive ? 'page' : false}
      target={props.external === true ? '_blank' : '_self'}
    >
      {props.children}
    </Link>
  );
};

type NavLinkProps = {
  key: string;
  text: string;
  url?: string;
  external?: boolean;
};

export type ParentNavLinkProps = NavLinkProps & { subItems?: (NavLinkProps | undefined)[] };

type NavProps = {
  links: (ParentNavLinkProps | undefined)[];
  nav: {
    navOpen: boolean;
    toggleNavOpen: (value: React.SetStateAction<boolean>) => void;
  };
  userSwap: {
    userSwapOpen: boolean;
    toggleUserSwapOpen: (value: React.SetStateAction<boolean>) => void;
  };
};

const renderLink = (
  link: ParentNavLinkProps | NavLinkProps | undefined,
  toggleNavOpen: (value: React.SetStateAction<boolean>) => void,
  offers: CampaignPriceOffer[],
) => {
  if (link === undefined) return false;
  return (
    <li key={link.key}>
      <NavLink
        to={link.url ?? `/${link.key}`}
        onClick={(): void => toggleNavOpen(false)}
        id={link.key}
        external={link.external === true ? true : false}
      >
        {link.text}
        {link.key === 'contracts' && offers && offers.length > 0 && <span className={styles.notification}></span>}
      </NavLink>
    </li>
  );
};

type SubItemWrapperProps = { link: ParentNavLinkProps; toggleNavOpen: (value: React.SetStateAction<boolean>) => void; offers: CampaignPriceOffer[] };

const SubItemWrapper: React.FunctionComponent<SubItemWrapperProps> = (props: SubItemWrapperProps) => {
  const { link, toggleNavOpen, offers } = props;

  const subItems = link.subItems?.filter((i) => i !== undefined);
  const subUrls = subItems?.map((i) => (i === undefined ? false : (i.url ?? i.key)));

  const active = subUrls?.includes(location.pathname.split('/')[1]);
  const [open, setOpen] = React.useState<boolean>(active ?? false);

  return (
    <li key={link.key} className={`${styles.parentWrapper} ${open ? styles.open : undefined}`}>
      <button
        className={`${styles.navLink} ${styles[link.key]} ${subUrls?.includes(location.pathname.split('/')[1]) ? `${styles.parentActive}` : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open ? true : false}
        aria-controls={`nav-${link.key}`}
      >
        {link.text}
      </button>
      <div className={styles.subItems} id={`nav-${link.key}`}>
        <ul>{subItems?.map((sublink) => renderLink(sublink, toggleNavOpen, offers))}</ul>
      </div>
    </li>
  );
};

const Navigation: React.FunctionComponent<NavProps> = (props: NavProps) => {
  const { offers } = OmaLummeApp.useState();

  const linkItems = props.links;
  const { navOpen, toggleNavOpen } = props.nav;
  const { t } = useTranslation();

  // Generate the links, including adding the active class if we are on that page.
  const menuLinks = linkItems?.map((link) => {
    if (link) {
      const subItems = link.subItems?.filter((i) => i !== undefined);
      if (subItems) {
        return <SubItemWrapper key={link.key} link={link} toggleNavOpen={toggleNavOpen} offers={offers} />;
      } else {
        return renderLink(link, toggleNavOpen, offers);
      }
    }
  });

  return (
    <nav className={`${styles.nav} ${navOpen ? styles.mobileOpen : ''}`} aria-label="Main navigation">
      <div className={`${styles.navLinks}`}>
        <ul>{menuLinks}</ul>
      </div>
      <div className={styles.navBottom}>
        <p className={styles.feedback}>
          <a href="https://response.questback.com/lummeenergiaoy/iwo7j3ag4u" target="_blank" rel="noopener noreferrer">
            {t('common:general.giveFeedback')}
          </a>
        </p>
      </div>
      <MobileMenuFooter nav={props.nav} userSwap={props.userSwap} />
    </nav>
  );
};

export default Navigation;
